import { useState, useRef, useEffect, useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { useProject } from '../context/ProjectContext';

// Status and priority types from the API
type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

// Attachment types
interface PendingAttachment {
  file: File;
  preview: string | null;
  base64Data: string;
}

interface TicketAttachment {
  url: string;
  filename: string;
  mimeType: string;
  size: number;
  type: 'image' | 'document' | 'audio' | 'video';
}

// Maximum file size (10MB)
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Allowed MIME types
const ALLOWED_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
  'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm',
  'video/mp4', 'video/webm', 'video/ogg',
  'application/pdf', 'text/plain', 'application/json',
];

/**
 * Support inbox page component.
 *
 * Displays support tickets with filtering capabilities and a detail panel
 * for viewing ticket threads and managing ticket status.
 */
export function SupportPage() {
  const { currentBotId } = useProject();
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [priorityFilter, setPriorityFilter] = useState<TicketPriority | ''>('');
  const [offset, setOffset] = useState(0);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const limit = 20;

  // Fetch tickets list
  const ticketsQuery = trpc.support.list.useQuery(
    {
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      offset,
      limit,
      includeClosed: statusFilter === 'closed',
      botId: currentBotId ?? undefined,
    },
    { enabled: !!currentBotId }
  );

  // Fetch stats for the header badge
  const statsQuery = trpc.support.stats.useQuery(
    { botId: currentBotId ?? undefined },
    { enabled: !!currentBotId }
  );

  // Fetch selected ticket details
  const ticketDetailQuery = trpc.support.get.useQuery(
    { id: selectedTicketId! },
    { enabled: !!selectedTicketId }
  );

  // Fetch support templates (canned responses) for quick replies
  const templatesQuery = trpc.support.templates.useQuery();

  const utils = trpc.useUtils();

  // Mutation for updating ticket status
  const updateMutation = trpc.support.update.useMutation({
    onSuccess: () => {
      utils.support.list.invalidate();
      utils.support.stats.invalidate();
      if (selectedTicketId) {
        utils.support.get.invalidate({ id: selectedTicketId });
      }
    },
  });

  // Mutation for claiming a ticket
  const claimMutation = trpc.support.claim.useMutation({
    onSuccess: () => {
      utils.support.list.invalidate();
      utils.support.stats.invalidate();
      if (selectedTicketId) {
        utils.support.get.invalidate({ id: selectedTicketId });
      }
    },
  });

  // Mutation for adding a message with attachments
  const addMessageMutation = trpc.support.addMessage.useMutation({
    onSuccess: () => {
      utils.support.list.invalidate();
      if (selectedTicketId) {
        utils.support.get.invalidate({ id: selectedTicketId });
      }
      setReplyMessage('');
      setPendingAttachments([]);
      setIsUploading(false);
    },
    onError: () => {
      setIsUploading(false);
    },
  });

  const handleResolve = (ticketId: string) => {
    if (confirm('Are you sure you want to resolve this ticket?')) {
      updateMutation.mutate({
        id: ticketId,
        status: 'resolved',
      });
    }
  };

  const handleClaim = (ticketId: string) => {
    claimMutation.mutate({ id: ticketId });
  };

  const handleStatusChange = (ticketId: string, newStatus: TicketStatus) => {
    updateMutation.mutate({
      id: ticketId,
      status: newStatus,
    });
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticketDetailQuery.data?.messages]);

  // Handle file selection for attachments
  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newAttachments: PendingAttachment[] = [];

    for (const file of Array.from(files)) {
      // Validate file type
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        alert(`File type not supported: ${file.name}`);
        continue;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        alert(`File too large (max 10MB): ${file.name}`);
        continue;
      }

      // Read file as base64
      const base64Data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result);
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });

      // Generate preview for images
      const preview = file.type.startsWith('image/') ? base64Data : null;

      newAttachments.push({ file, preview, base64Data });
    }

    setPendingAttachments((prev) => [...prev, ...newAttachments].slice(0, 10)); // Max 10 attachments

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Remove a pending attachment
  const handleRemoveAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Send reply with text and/or attachments
  const handleSendReply = () => {
    const hasContent = replyMessage.trim().length > 0;
    const hasAttachments = pendingAttachments.length > 0;

    if (!hasContent && !hasAttachments) return;
    if (!selectedTicketId) return;

    setIsUploading(true);

    const attachments = pendingAttachments.map((a) => ({
      filename: a.file.name,
      mimeType: a.file.type,
      base64Data: a.base64Data,
    }));

    addMessageMutation.mutate({
      ticketId: selectedTicketId,
      content: replyMessage,
      attachments: hasAttachments ? attachments : undefined,
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !isUploading) {
      e.preventDefault();
      handleSendReply();
    }
  };

  // Handle canned response selection
  const handleCannedResponseSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const templateId = e.target.value;
    if (!templateId) return;

    const template = templatesQuery.data?.find((t) => t.id === templateId);
    if (!template) return;

    // If there's existing text, append the template content with a separator
    if (replyMessage.trim()) {
      setReplyMessage((prev) => prev + '\n\n---\n\n' + template.content);
    } else {
      setReplyMessage(template.content);
    }

    // Reset the dropdown
    e.target.value = '';
  };

  // Helper to get user initials
  const getInitials = (name: string | null | undefined): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Helper to format relative time
  const getRelativeTime = (date: string | Date | null | undefined): string => {
    if (!date) return '';
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  // Helper to check if a ticket is unread (no adminLastViewedAt or updatedAt > adminLastViewedAt)
  const isTicketUnread = (ticket: { adminLastViewedAt: string | null; updatedAt: string | null }): boolean => {
    if (!ticket.adminLastViewedAt) return true;
    if (!ticket.updatedAt) return false;
    return new Date(ticket.updatedAt) > new Date(ticket.adminLastViewedAt);
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Support Inbox</h1>
        {statsQuery.data && (
          <div style={styles.statsContainer}>
            <span style={styles.openBadge}>
              {statsQuery.data.byStatus.open} open
            </span>
            <span style={styles.inProgressBadge}>
              {statsQuery.data.byStatus.inProgress} in progress
            </span>
            {statsQuery.data.unassigned > 0 && (
              <span style={styles.unassignedBadge}>
                {statsQuery.data.unassigned} unassigned
              </span>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as TicketStatus | '');
            setOffset(0);
            setSelectedTicketId(null);
          }}
          style={styles.select}
        >
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => {
            setPriorityFilter(e.target.value as TicketPriority | '');
            setOffset(0);
            setSelectedTicketId(null);
          }}
          style={styles.select}
        >
          <option value="">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Main content area with list and detail panel */}
      <div style={styles.mainContent}>
        {/* Tickets List */}
        <div style={styles.listPanel}>
          {ticketsQuery.isLoading ? (
            <div style={styles.loading}>Loading tickets...</div>
          ) : ticketsQuery.error ? (
            <div style={styles.error}>
              Error loading tickets: {ticketsQuery.error.message}
            </div>
          ) : ticketsQuery.data?.tickets.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📬</div>
              <h3 style={styles.emptyTitle}>No tickets found</h3>
              <p style={styles.emptyText}>
                {statusFilter || priorityFilter
                  ? 'Try adjusting your filters'
                  : 'No support tickets at this time.'}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.ticketsList}>
                {ticketsQuery.data?.tickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    style={{
                      ...styles.ticketCard,
                      ...(selectedTicketId === ticket.id ? styles.ticketCardSelected : {}),
                    }}
                    onClick={() => setSelectedTicketId(ticket.id)}
                  >
                    <div style={styles.ticketRow}>
                      {/* Unread Indicator */}
                      {isTicketUnread(ticket) && (
                        <div style={styles.unreadIndicator} title="New activity" />
                      )}

                      {/* User Avatar */}
                      <div style={styles.ticketAvatar}>
                        {getInitials(ticket.user?.name)}
                      </div>

                      {/* Ticket Content */}
                      <div style={styles.ticketContent}>
                        <div style={styles.ticketTopRow}>
                          <span style={styles.ticketUserName}>
                            {ticket.user?.name || 'Unknown User'}
                          </span>
                          <span style={styles.ticketTime}>
                            {getRelativeTime(ticket.createdAt)}
                          </span>
                        </div>

                        <h3 style={styles.ticketSubject}>{ticket.subject}</h3>

                        <p style={styles.ticketPreview}>
                          {ticket.initialMessage.length > 80
                            ? ticket.initialMessage.substring(0, 80) + '...'
                            : ticket.initialMessage}
                        </p>

                        <div style={styles.ticketBadges}>
                          <span
                            style={{
                              ...styles.priorityBadge,
                              ...getPriorityStyle(ticket.priority || ''),
                            }}
                          >
                            {ticket.priority}
                          </span>
                          <span
                            style={{
                              ...styles.statusBadge,
                              ...getStatusStyle(ticket.status || ''),
                            }}
                          >
                            {formatStatus(ticket.status || '')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {ticketsQuery.data && ticketsQuery.data.pagination.total > limit && (
                <div style={styles.pagination}>
                  <button
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    disabled={offset === 0}
                    style={styles.pageButton}
                  >
                    Previous
                  </button>
                  <span style={styles.pageInfo}>
                    {offset + 1} - {Math.min(offset + limit, ticketsQuery.data.pagination.total)} of {ticketsQuery.data.pagination.total}
                  </span>
                  <button
                    onClick={() => setOffset((o) => o + limit)}
                    disabled={!ticketsQuery.data.pagination.hasMore}
                    style={styles.pageButton}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Detail Panel */}
        <div style={styles.detailPanel}>
          {!selectedTicketId ? (
            <div style={styles.noSelection}>
              <div style={styles.noSelectionIcon}>📋</div>
              <h3 style={styles.noSelectionTitle}>No ticket selected</h3>
              <p style={styles.noSelectionText}>
                Select a ticket from the list to view the conversation
              </p>
            </div>
          ) : ticketDetailQuery.isLoading ? (
            <div style={styles.loading}>Loading ticket details...</div>
          ) : ticketDetailQuery.error ? (
            <div style={styles.error}>
              Error: {ticketDetailQuery.error.message}
            </div>
          ) : ticketDetailQuery.data ? (
            <div style={styles.detailContent}>
              {/* Conversation Header */}
              <div style={styles.conversationHeader}>
                <div style={styles.conversationHeaderLeft}>
                  <div style={styles.headerAvatar}>
                    {getInitials(ticketDetailQuery.data.user?.name)}
                  </div>
                  <div style={styles.headerInfo}>
                    <h2 style={styles.headerUserName}>
                      {ticketDetailQuery.data.user?.name || 'Unknown User'}
                    </h2>
                    <p style={styles.headerSubject}>{ticketDetailQuery.data.subject}</p>
                  </div>
                </div>
                <div style={styles.conversationHeaderRight}>
                  <div style={styles.headerBadges}>
                    <span
                      style={{
                        ...styles.priorityBadge,
                        ...getPriorityStyle(ticketDetailQuery.data.priority || ''),
                      }}
                    >
                      {ticketDetailQuery.data.priority}
                    </span>
                    <span
                      style={{
                        ...styles.statusBadge,
                        ...getStatusStyle(ticketDetailQuery.data.status || ''),
                      }}
                    >
                      {formatStatus(ticketDetailQuery.data.status || '')}
                    </span>
                  </div>
                  {ticketDetailQuery.data.user?.platform && (
                    <span style={styles.platformTag}>
                      {ticketDetailQuery.data.user.platform}
                    </span>
                  )}
                </div>
              </div>

              {/* Ticket Meta Info */}
              <div style={styles.ticketMeta}>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Assigned to</span>
                  <span style={styles.metaValue}>
                    {ticketDetailQuery.data.assignedAdmin?.name || 'Unassigned'}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span style={styles.metaLabel}>Created</span>
                  <span style={styles.metaValue}>
                    {ticketDetailQuery.data.createdAt
                      ? new Date(ticketDetailQuery.data.createdAt).toLocaleDateString()
                      : 'Unknown'}
                  </span>
                </div>
                {ticketDetailQuery.data.resolvedAt && (
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Resolved</span>
                    <span style={styles.metaValue}>
                      {new Date(ticketDetailQuery.data.resolvedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              {(ticketDetailQuery.data.status === 'open' ||
                ticketDetailQuery.data.status === 'in_progress') && (
                <div style={styles.detailActions}>
                  {!ticketDetailQuery.data.assignedAdmin && (
                    <button
                      onClick={() => handleClaim(ticketDetailQuery.data!.id)}
                      disabled={claimMutation.isPending}
                      style={styles.claimButton}
                    >
                      {claimMutation.isPending ? 'Claiming...' : 'Claim Ticket'}
                    </button>
                  )}
                  {ticketDetailQuery.data.status === 'open' && (
                    <button
                      onClick={() =>
                        handleStatusChange(ticketDetailQuery.data!.id, 'in_progress')
                      }
                      disabled={updateMutation.isPending}
                      style={styles.progressButton}
                    >
                      Start Progress
                    </button>
                  )}
                  <button
                    onClick={() => handleResolve(ticketDetailQuery.data!.id)}
                    disabled={updateMutation.isPending}
                    style={styles.resolveButton}
                  >
                    {updateMutation.isPending ? 'Resolving...' : 'Resolve'}
                  </button>
                </div>
              )}

              {/* Message Thread - Chat Bubbles */}
              <div style={styles.messagesContainer}>
                {/* Initial message - User bubble (left aligned) */}
                <div style={styles.messageBubbleWrapper}>
                  <div style={styles.bubbleAvatar}>
                    {getInitials(ticketDetailQuery.data.user?.name)}
                  </div>
                  <div style={styles.bubbleContent}>
                    <div style={styles.bubbleHeader}>
                      <span style={styles.bubbleSender}>
                        {ticketDetailQuery.data.user?.name || 'User'}
                      </span>
                      <span style={styles.bubbleTime}>
                        {ticketDetailQuery.data.createdAt
                          ? new Date(ticketDetailQuery.data.createdAt).toLocaleString()
                          : ''}
                      </span>
                    </div>
                    <div style={styles.userBubble}>
                      {ticketDetailQuery.data.initialMessage}
                    </div>
                  </div>
                </div>

                {/* Thread messages */}
                {ticketDetailQuery.data.messages.map((message) => {
                  const isHidden = message.senderType === 'system' || message.senderType === 'bot';
                  return (
                  <div
                    key={message.id}
                    style={{
                      ...styles.messageBubbleWrapper,
                      ...(isHidden
                        ? styles.hiddenBubbleWrapper
                        : message.senderType === 'admin'
                          ? styles.adminBubbleWrapper
                          : {}),
                    }}
                  >
                    {!isHidden && message.senderType !== 'admin' && (
                      <div style={styles.bubbleAvatar}>
                        {getInitials(ticketDetailQuery.data?.user?.name)}
                      </div>
                    )}
                    <div
                      style={{
                        ...styles.bubbleContent,
                        ...(isHidden
                          ? styles.hiddenBubbleContent
                          : message.senderType === 'admin'
                            ? styles.adminBubbleContent
                            : {}),
                      }}
                    >
                      <div
                        style={{
                          ...styles.bubbleHeader,
                          ...(isHidden
                            ? styles.hiddenBubbleHeader
                            : message.senderType === 'admin'
                              ? styles.adminBubbleHeader
                              : {}),
                        }}
                      >
                        {isHidden && (
                          <span style={styles.hiddenBadge}>System Context</span>
                        )}
                        <span style={styles.bubbleSender}>
                          {isHidden ? 'System' : message.senderType === 'admin' ? 'Support Agent' : 'User'}
                        </span>
                        <span style={styles.bubbleTime}>
                          {message.createdAt
                            ? new Date(message.createdAt).toLocaleString()
                            : ''}
                        </span>
                      </div>
                      <div
                        style={
                          isHidden
                            ? styles.hiddenBubble
                            : message.senderType === 'admin'
                              ? styles.adminBubble
                              : styles.userBubble
                        }
                      >
                        {message.content}
                        {/* Render attachments if present */}
                        {message.attachments && message.attachments.length > 0 && (
                          <div style={styles.messageAttachmentsContainer}>
                            {(message.attachments as TicketAttachment[]).map((attachment, idx) => (
                              <div key={idx} style={styles.messageAttachmentItem}>
                                {attachment.type === 'image' ? (
                                  <a
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={styles.attachmentImageLink}
                                  >
                                    <img
                                      src={attachment.url}
                                      alt={attachment.filename}
                                      style={styles.messageAttachmentImage}
                                    />
                                  </a>
                                ) : (
                                  <a
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      ...styles.messageAttachmentFile,
                                      ...(message.senderType === 'admin'
                                        ? styles.adminAttachmentFile
                                        : {}),
                                    }}
                                  >
                                    <span style={styles.attachmentFileIcon}>
                                      {attachment.type === 'document' ? '📄' :
                                       attachment.type === 'audio' ? '🎵' :
                                       attachment.type === 'video' ? '🎬' : '📎'}
                                    </span>
                                    <span style={styles.attachmentFileName}>
                                      {attachment.filename}
                                    </span>
                                    <span style={styles.attachmentFileSize}>
                                      ({formatFileSize(attachment.size)})
                                    </span>
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {!isHidden && message.senderType === 'admin' && (
                      <div style={styles.adminAvatar}>
                        <span style={styles.adminAvatarIcon}>👤</span>
                      </div>
                    )}
                  </div>
                );})}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply Box */}
              {(ticketDetailQuery.data.status === 'open' ||
                ticketDetailQuery.data.status === 'in_progress') && (
                <div style={styles.replyBox}>
                  {/* Canned Responses Dropdown */}
                  {templatesQuery.data && templatesQuery.data.length > 0 && (
                    <div style={styles.cannedResponsesContainer}>
                      <label style={styles.cannedResponsesLabel}>Canned Responses:</label>
                      <select
                        onChange={handleCannedResponseSelect}
                        style={styles.cannedResponsesSelect}
                        defaultValue=""
                        disabled={templatesQuery.isLoading || isUploading}
                      >
                        <option value="" disabled>
                          {templatesQuery.isLoading ? 'Loading templates...' : 'Select a template...'}
                        </option>
                        {templatesQuery.data.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Pending Attachments Preview */}
                  {pendingAttachments.length > 0 && (
                    <div style={styles.attachmentsPreviewContainer}>
                      {pendingAttachments.map((attachment, index) => (
                        <div key={index} style={styles.attachmentPreviewItem}>
                          {attachment.preview ? (
                            <img
                              src={attachment.preview}
                              alt={attachment.file.name}
                              style={styles.attachmentPreviewImage}
                            />
                          ) : (
                            <div style={styles.attachmentPreviewDoc}>
                              <span style={styles.attachmentPreviewDocIcon}>
                                {attachment.file.type.includes('pdf') ? '📄' :
                                 attachment.file.type.includes('audio') ? '🎵' :
                                 attachment.file.type.includes('video') ? '🎬' : '📎'}
                              </span>
                            </div>
                          )}
                          <span style={styles.attachmentPreviewName}>
                            {attachment.file.name.length > 15
                              ? attachment.file.name.substring(0, 12) + '...'
                              : attachment.file.name}
                          </span>
                          <button
                            onClick={() => handleRemoveAttachment(index)}
                            style={styles.attachmentRemoveButton}
                            title="Remove attachment"
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your reply..."
                    style={styles.replyTextarea}
                    rows={3}
                    disabled={isUploading}
                  />
                  <div style={styles.replyActions}>
                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      multiple
                      accept={ALLOWED_MIME_TYPES.join(',')}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      style={styles.attachButton}
                      disabled={isUploading || pendingAttachments.length >= 10}
                      title="Attach files (max 10)"
                    >
                      📎 Attach
                    </button>
                    <button
                      onClick={handleSendReply}
                      style={{
                        ...styles.sendButton,
                        ...((!replyMessage.trim() && pendingAttachments.length === 0) || isUploading
                          ? styles.sendButtonDisabled
                          : {}),
                      }}
                      disabled={(!replyMessage.trim() && pendingAttachments.length === 0) || isUploading}
                    >
                      {isUploading ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function formatStatus(status: string): string {
  switch (status) {
    case 'in_progress':
      return 'In Progress';
    case 'open':
      return 'Open';
    case 'resolved':
      return 'Resolved';
    case 'closed':
      return 'Closed';
    default:
      return status;
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function getPriorityStyle(priority: string): React.CSSProperties {
  switch (priority) {
    case 'urgent':
      return { backgroundColor: '#fee2e2', color: '#991b1b' };
    case 'high':
      return { backgroundColor: '#ffedd5', color: '#c2410c' };
    case 'medium':
      return { backgroundColor: '#fef3c7', color: '#92400e' };
    case 'low':
      return { backgroundColor: '#f0fdf4', color: '#166534' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569' };
  }
}

function getStatusStyle(status: string): React.CSSProperties {
  switch (status) {
    case 'open':
      return { backgroundColor: '#dbeafe', color: '#1e40af' };
    case 'in_progress':
      return { backgroundColor: '#fef9c3', color: '#854d0e' };
    case 'resolved':
      return { backgroundColor: '#dcfce7', color: '#166534' };
    case 'closed':
      return { backgroundColor: '#f1f5f9', color: '#475569' };
    default:
      return { backgroundColor: '#f1f5f9', color: '#475569' };
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '1400px',
    height: 'calc(100vh - 48px)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  statsContainer: {
    display: 'flex',
    gap: '8px',
  },
  openBadge: {
    padding: '6px 12px',
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: '500',
  },
  inProgressBadge: {
    padding: '6px 12px',
    backgroundColor: '#fef9c3',
    color: '#854d0e',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: '500',
  },
  unassignedBadge: {
    padding: '6px 12px',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: '500',
  },
  filters: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
  },
  select: {
    padding: '8px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
  },
  mainContent: {
    display: 'grid',
    gridTemplateColumns: '380px 1fr',
    gap: '20px',
    flex: 1,
    minHeight: 0,
  },
  listPanel: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    padding: '12px',
  },
  detailPanel: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#64748b',
  },
  error: {
    padding: '16px',
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    margin: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
  },
  emptyText: {
    fontSize: '14px',
    color: '#64748b',
  },
  ticketsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    overflow: 'auto',
    flex: 1,
  },
  ticketCard: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '14px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    border: '1px solid transparent',
  },
  ticketCardSelected: {
    borderColor: '#3b82f6',
    backgroundColor: '#f0f7ff',
    boxShadow: '0 0 0 1px #3b82f6',
  },
  ticketRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  unreadIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    flexShrink: 0,
    marginTop: '16px',
  },
  ticketAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569',
    flexShrink: 0,
  },
  ticketContent: {
    flex: 1,
    minWidth: 0,
  },
  ticketTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  ticketUserName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1e293b',
  },
  ticketTime: {
    fontSize: '12px',
    color: '#94a3b8',
  },
  ticketSubject: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '4px',
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ticketPreview: {
    fontSize: '12px',
    color: '#64748b',
    lineHeight: '1.4',
    margin: '4px 0 8px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  ticketBadges: {
    display: 'flex',
    gap: '6px',
  },
  priorityBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '10px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  statusBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '10px',
    fontWeight: '500',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid #e2e8f0',
  },
  pageButton: {
    padding: '6px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#ffffff',
    fontSize: '13px',
    cursor: 'pointer',
  },
  pageInfo: {
    fontSize: '13px',
    color: '#64748b',
  },
  // No selection state
  noSelection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: '40px',
  },
  noSelectionIcon: {
    fontSize: '56px',
    marginBottom: '16px',
    opacity: 0.5,
  },
  noSelectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#475569',
    margin: '0 0 8px',
  },
  noSelectionText: {
    fontSize: '14px',
    color: '#94a3b8',
    textAlign: 'center',
    margin: 0,
  },
  // Detail panel content
  detailContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  // Conversation header
  conversationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#ffffff',
  },
  conversationHeaderLeft: {
    display: 'flex',
    gap: '14px',
    alignItems: 'center',
  },
  headerAvatar: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  headerUserName: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    margin: 0,
  },
  headerSubject: {
    fontSize: '14px',
    color: '#64748b',
    margin: 0,
  },
  conversationHeaderRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '8px',
  },
  headerBadges: {
    display: 'flex',
    gap: '6px',
  },
  platformTag: {
    padding: '4px 8px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500',
    textTransform: 'uppercase',
  },
  // Ticket meta info bar
  ticketMeta: {
    display: 'flex',
    gap: '24px',
    padding: '12px 24px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  metaLabel: {
    fontSize: '11px',
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  metaValue: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#334155',
  },
  // Action buttons
  detailActions: {
    display: 'flex',
    gap: '8px',
    padding: '12px 24px',
    borderBottom: '1px solid #e2e8f0',
  },
  claimButton: {
    padding: '8px 16px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  progressButton: {
    padding: '8px 16px',
    backgroundColor: '#f59e0b',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  resolveButton: {
    padding: '8px 16px',
    backgroundColor: '#22c55e',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  // Messages container - chat bubbles
  messagesContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: '#fafbfc',
  },
  messageBubbleWrapper: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    maxWidth: '80%',
  },
  adminBubbleWrapper: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  bubbleAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    flexShrink: 0,
  },
  adminAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  adminAvatarIcon: {
    fontSize: '16px',
  },
  bubbleContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  adminBubbleContent: {
    alignItems: 'flex-end',
  },
  bubbleHeader: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  adminBubbleHeader: {
    flexDirection: 'row-reverse',
  },
  bubbleSender: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#334155',
  },
  bubbleTime: {
    fontSize: '11px',
    color: '#94a3b8',
  },
  userBubble: {
    backgroundColor: '#ffffff',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    borderTopLeftRadius: '4px',
    padding: '12px 16px',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#334155',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  adminBubble: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    borderRadius: '12px',
    borderTopRightRadius: '4px',
    padding: '12px 16px',
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  // Hidden/System Context message styles
  hiddenBubbleWrapper: {
    alignSelf: 'center',
    maxWidth: '90%',
  },
  hiddenBubbleContent: {
    alignItems: 'center',
  },
  hiddenBubbleHeader: {
    justifyContent: 'center',
  },
  hiddenBubble: {
    backgroundColor: '#e5e7eb',
    color: '#6b7280',
    borderRadius: '8px',
    border: '1px dashed #9ca3af',
    padding: '12px 16px',
    fontSize: '14px',
    lineHeight: '1.5',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  hiddenBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    backgroundColor: '#9ca3af',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 600,
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginRight: '8px',
  },
  // Reply box
  replyBox: {
    borderTop: '1px solid #e2e8f0',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
  },
  cannedResponsesContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  cannedResponsesLabel: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    whiteSpace: 'nowrap',
  },
  cannedResponsesSelect: {
    flex: 1,
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    outline: 'none',
    color: '#334155',
  },
  replyTextarea: {
    width: '100%',
    padding: '12px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    resize: 'none',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box',
  },
  replyActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '12px',
  },
  sendButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  sendButtonDisabled: {
    backgroundColor: '#94a3b8',
    cursor: 'not-allowed',
  },
  // Attach button styles
  attachButton: {
    padding: '10px 16px',
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    marginRight: '8px',
  },
  // Pending attachments preview styles
  attachmentsPreviewContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '12px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  attachmentPreviewItem: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '8px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
    width: '80px',
  },
  attachmentPreviewImage: {
    width: '60px',
    height: '60px',
    objectFit: 'cover',
    borderRadius: '4px',
  },
  attachmentPreviewDoc: {
    width: '60px',
    height: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: '4px',
  },
  attachmentPreviewDocIcon: {
    fontSize: '24px',
  },
  attachmentPreviewName: {
    fontSize: '10px',
    color: '#64748b',
    marginTop: '4px',
    textAlign: 'center',
    wordBreak: 'break-all',
  },
  attachmentRemoveButton: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    width: '20px',
    height: '20px',
    border: 'none',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    color: '#ffffff',
    fontSize: '14px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
  },
  // Message attachments display styles
  messageAttachmentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '8px',
  },
  messageAttachmentItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  messageAttachmentImage: {
    maxWidth: '200px',
    maxHeight: '200px',
    borderRadius: '8px',
    objectFit: 'cover',
    cursor: 'pointer',
  },
  attachmentImageLink: {
    display: 'block',
  },
  messageAttachmentFile: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '6px',
    textDecoration: 'none',
    color: 'inherit',
  },
  adminAttachmentFile: {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
  },
  attachmentFileIcon: {
    fontSize: '20px',
  },
  attachmentFileName: {
    fontSize: '13px',
    fontWeight: '500',
    maxWidth: '150px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  attachmentFileSize: {
    fontSize: '11px',
    opacity: 0.8,
  },
};

export default SupportPage;
